import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ConnectorParameterRestoreEntry,
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
  Screen,
  Option,
  ModalState,
  FormField,
} from '../types';
import { getSynchronousLocalStorage } from '../lib/utils/synchronousLocalStorage';
import { requestHostWindowResizeForScreen } from '../lib/utils/windowSizing';
import {
  logSetupRevision,
  reconcileHydratedProjectScope,
  resolveVerifiedRevisionToken,
} from '../lib/setupRevision';
import { clearedSetupRevisionState, createSetupRevisionActions } from './setupRevisionSlice';
import {
  getCurrentHistoryIndex,
  normalizeNavigationState,
} from './uiNavigationState';
import { initialSectionsState, sanitizeSectionsState } from './uiSectionsState';
import { partializeUIState, validateAndMergeState } from './uiStatePersistence';
import { initialState, type UIStore } from './uiStoreState';

// Re-export types for backward compatibility
export type { Screen, Option, ModalState, FormField };
export type {
  ConnectorParameterRestoreEntry,
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
};
export type {
  AdditionalCategoriesSection,
  BoltConnectionSection,
  ConnectorBindingSection,
  ProcessPropertiesSection,
  WorkflowSectionsState,
} from './uiSectionsState';

const STORAGE_VERSION = 3;

// ============================================================================
// Store Creation
// ============================================================================

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Navigation actions
      setScreen: (screen) => {
        const { screen: currentScreen, navigationHistory, selectedOption } = get();
        if (currentScreen === screen) return;
        const normalizedNavigation = normalizeNavigationState(
          screen,
          [...navigationHistory, screen],
          selectedOption
        );
        requestHostWindowResizeForScreen(normalizedNavigation.screen);
        set(normalizedNavigation);
      },

      navigateTo: (screen) => {
        const {
          screen: currentScreen,
          navigationHistory,
          selectedOption,
        } = get();
        if (currentScreen === screen) return;
        const normalizedNavigation = normalizeNavigationState(
          screen,
          [...navigationHistory, screen],
          selectedOption
        );
        requestHostWindowResizeForScreen(normalizedNavigation.screen);
        set(normalizedNavigation);
      },

      jumpTo: (screen, history) => {
        const {
          screen: currentScreen,
          navigationHistory,
          selectedOption,
        } = get();
        const nextHistory = history && history.length > 0
          ? [...navigationHistory, ...history, screen]
          : [...navigationHistory, screen];
        const normalizedNavigation = normalizeNavigationState(
          screen,
          nextHistory,
          selectedOption
        );

        if (
          currentScreen === normalizedNavigation.screen &&
          JSON.stringify(navigationHistory) === JSON.stringify(normalizedNavigation.navigationHistory)
        ) {
          return;
        }

        requestHostWindowResizeForScreen(normalizedNavigation.screen);
        set(normalizedNavigation);
      },

      goBack: () => {
        const { navigationHistory, screen } = get();
        const currentHistoryIndex = getCurrentHistoryIndex(navigationHistory, screen);
        if (currentHistoryIndex <= 0) return;
        const previousScreen = navigationHistory[currentHistoryIndex - 1];

        requestHostWindowResizeForScreen(previousScreen);
        set({
          screen: previousScreen
        });
      },

      setNavigationHistory: (history) => {
        const {
          navigationHistory: currentHistory,
          screen,
          selectedOption,
        } = get();
        const normalizedNavigation = normalizeNavigationState(screen, history, selectedOption);
        if (
          JSON.stringify(currentHistory) === JSON.stringify(normalizedNavigation.navigationHistory)
        ) {
          return;
        }
        set(normalizedNavigation);
      },

      clearNavigationHistory: () => {
        const { screen, selectedOption } = get();
        const normalizedNavigation = normalizeNavigationState(
          screen,
          [screen],
          selectedOption
        );
        set({ navigationHistory: normalizedNavigation.navigationHistory });
      },

      setConnectorBindingSection: (section) => {
        if (get().sections.connectorBinding === section) return;
        set((state) => ({
          sections: {
            ...state.sections,
            connectorBinding: section,
          },
        }));
      },

      setBoltConnectionSection: (section) => {
        if (get().sections.boltConnection === section) return;
        set((state) => ({
          sections: {
            ...state.sections,
            boltConnection: section,
          },
        }));
      },

      setProcessPropertiesSection: (section) => {
        if (get().sections.processProperties === section) return;
        set((state) => ({
          sections: {
            ...state.sections,
            processProperties: section,
          },
        }));
      },

      setAdditionalCategoriesSection: (section) => {
        if (get().sections.additionalCategories === section) return;
        set((state) => ({
          sections: {
            ...state.sections,
            additionalCategories: section,
          },
        }));
      },

      restoreWizardProfileProgress: (progress) => {
        if (!progress) {
          return;
        }

        const { selectedOption } = get();
        const normalizedNavigation = normalizeNavigationState(
          progress.screen,
          progress.navigationHistory,
          selectedOption
        );
        requestHostWindowResizeForScreen(normalizedNavigation.screen);
        set((state) => ({
          ...normalizedNavigation,
          sections: sanitizeSectionsState(progress.sections, state.selectedOption),
        }));
      },

      // Option actions
      setSelectedOption: (option) => {
        const {
          selectedOption: currentOption,
          screen,
          navigationHistory,
        } = get();
        if (currentOption === option) return;
        const normalizedNavigation = normalizeNavigationState(
          screen,
          navigationHistory,
          option
        );
        set({
          selectedOption: option,
          sections: sanitizeSectionsState(get().sections, option),
          ...normalizedNavigation,
        });
      },

      selectWorkflowFromMenu: (option) => {
        const normalizedNavigation = normalizeNavigationState('menu', ['welcome', 'menu'], option);
        set({
          selectedOption: option,
          sections: sanitizeSectionsState(initialSectionsState, option),
          defaultPropertiesConfirmed: false,
          processConditionsChecked: false,
          projectPropertiesChecked: false,
          connectorParameterUndoPlan: null,
          boltParameterUndoPlan: null,
          additionalCategoryParameterUndoPlan: null,
          ...clearedSetupRevisionState,
          ...normalizedNavigation,
        });
      },

      // Setup revision actions
      ...createSetupRevisionActions(set, get, {
        onScreenChanged: requestHostWindowResizeForScreen,
      }),

      // Modal actions
      openModal: (modalType, modalData = undefined) => {
        set({
          modal: {
            isOpen: true,
            modalType,
            modalData
          }
        });
      },

      closeModal: () => {
        set({
          modal: {
            isOpen: false,
            modalType: null,
            modalData: undefined
          }
        });
      },

      // Initialization actions
      setInitialized: (isInitialized) => {
        set({ isInitialized });
      },

      // Confirmation actions
      setDefaultPropertiesConfirmed: (confirmed) => {
        if (get().defaultPropertiesConfirmed === confirmed) return;
        set({ defaultPropertiesConfirmed: confirmed });
      },

      setProcessConditionsChecked: (checked) => {
        if (get().processConditionsChecked === checked) return;
        set({ processConditionsChecked: checked });
      },

      setProjectPropertiesChecked: (checked) => {
        if (get().projectPropertiesChecked === checked) return;
        set({ projectPropertiesChecked: checked });
      },

      setConnectorParameterUndoPlan: (plan) => {
        if (get().connectorParameterUndoPlan === plan) return;
        set({ connectorParameterUndoPlan: plan });
      },

      setBoltParameterUndoPlan: (plan) => {
        if (get().boltParameterUndoPlan === plan) return;
        set({ boltParameterUndoPlan: plan });
      },

      setAdditionalCategoryParameterUndoPlan: (plan) => {
        if (get().additionalCategoryParameterUndoPlan === plan) return;
        set({ additionalCategoryParameterUndoPlan: plan });
      },

      resetProjectScopedRuntimeState: () => {
        set({
          defaultPropertiesConfirmed: false,
          connectorParameterUndoPlan: null,
          boltParameterUndoPlan: null,
          additionalCategoryParameterUndoPlan: null,
          // The revision id is deliberately kept: it must never rewind, so a
          // late result from the previous project can still be recognised.
          ...clearedSetupRevisionState,
        });
      },

      reconcileProjectScopedRuntimeState: (activeProjectKey) => {
        const state = get();
        const decision = reconcileHydratedProjectScope(activeProjectKey, {
          setupRevisionProjectKey: state.setupRevisionProjectKey,
          verifiedSetupBaseline: state.verifiedSetupBaseline,
          undoPlans: [
            state.connectorParameterUndoPlan,
            state.boltParameterUndoPlan,
            state.additionalCategoryParameterUndoPlan,
          ],
        });

        if (decision.kind === 'reset') {
          logSetupRevision('projectScopeReset', {
            revisionId: state.setupRevisionId,
            phase: state.setupRevisionStatus,
            projectKey: activeProjectKey,
            scopeReset: decision.reason,
          });
          get().resetProjectScopedRuntimeState();
        }

        return decision;
      },

      // Reset action
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'pipeex-ui-store',
      version: STORAGE_VERSION,
      storage: createJSONStorage(getSynchronousLocalStorage),
      partialize: partializeUIState,
      merge: (persistedState, currentState) =>
        validateAndMergeState(persistedState, currentState),
    }
  )
);

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

export const useCurrentScreen = () => useUIStore((state) => state.screen);
export const useNavigationHistory = () => useUIStore((state) => state.navigationHistory);
export const useSelectedOption = () => useUIStore((state) => state.selectedOption);
export const useConnectorBindingSection = () =>
  useUIStore((state) => state.sections.connectorBinding);
export const useBoltConnectionSection = () => useUIStore((state) => state.sections.boltConnection);
export const useProcessPropertiesSection = () =>
  useUIStore((state) => state.sections.processProperties);
export const useAdditionalCategoriesSection = () =>
  useUIStore((state) => state.sections.additionalCategories);
export const useModal = () => useUIStore((state) => state.modal);
export const useIsInitialized = () => useUIStore((state) => state.isInitialized);
export const useSetupRevisionStatus = () => useUIStore((state) => state.setupRevisionStatus);
export const usePendingNavigationIntent = () =>
  useUIStore((state) => state.pendingNavigationIntent);

/**
 * The evidence of the last verified setup, or `null` when nothing has been
 * proven yet. Discard reverts to this record rather than to the profile that
 * happens to be selected, so it must be read from the store, never rebuilt.
 */
export const useVerifiedSetupBaseline = () =>
  useUIStore((state) => state.verifiedSetupBaseline);

/**
 * Opaque token naming the setup revision that is currently verified, or `null`
 * while a revision is open or nothing has been verified yet. Consumers cache
 * evidence against it and discard that evidence whenever the token changes.
 */
export const useVerifiedSetupRevision = (activeProfileKey = '') =>
  useUIStore((state) =>
    resolveVerifiedRevisionToken({
      setupRevisionId: state.setupRevisionId,
      setupRevisionStatus: state.setupRevisionStatus,
      verifiedSetupFingerprint: state.verifiedSetupBaseline?.setupFingerprint ?? null,
      activeProfileKey,
    })
  );
